import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CheckSquareIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import {
  WORKBOOK_MAX_TASK_RULES,
  type WorkbookDefinition,
  type WorkbookTaskRule,
} from '@erp/shared-types';
import { orpc } from '@/api/rpc/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UserSelect } from '@/components/common/UserSelect';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import { useCan } from '@/hooks/useCan';
import { Action, Subject } from '@erp/authz';
import { tasksHomePath } from '@/pages/authenticated/tasks/$taskId/taskDetailCrumbs';
import {
  addTaskRule,
  removeTaskRule,
  taskTitleColumns,
  updateTaskRule,
  TASK_PRIORITIES,
} from './workbookTaskRules';
import {
  useTaskPriorityLabels,
  useTaskStatusLabel,
} from './taskPriorityLabels';
import type { AutomationsSectionProps } from './workbookIntegrations';
import { IconTooltip } from '@/components/common/IconTooltip';

/** Tasks' Automations section: the Workbook's Task rules and the Tasks it opened. */
export function WorkbookTasksSection({
  editor,
  canUpdate,
}: AutomationsSectionProps) {
  const { definition } = editor;
  const update = (
    change: (definition: WorkbookDefinition) => WorkbookDefinition,
  ) => editor.edits.workbook.update(change);
  const full = definition.taskRules.length >= WORKBOOK_MAX_TASK_RULES;
  return (
    <section
      aria-labelledby="workbook-automations-tasks"
      className="space-y-2 rounded-lg border p-3"
    >
      <h3
        id="workbook-automations-tasks"
        className="flex items-center gap-2 text-sm font-medium"
      >
        <CheckSquareIcon className="size-4 text-muted-foreground" />
        <T context="workbook automations tasks section">Tasks</T>
      </h3>
      <p className="text-sm text-muted-foreground">
        <T context="workbook task rules help">
          After each completed run, open a task for every row a worksheet
          returns, such as a worksheet of exceptions that need a person. Each
          task links back to this workbook. At most 100 tasks per rule per run.
        </T>
      </p>
      {definition.taskRules.length > 0 && (
        <ul className="space-y-2">
          {definition.taskRules.map((rule) => (
            <TaskRuleRow
              key={rule.id}
              rule={rule}
              definition={definition}
              disabled={!canUpdate}
              onChange={(patch) =>
                update((latest) => updateTaskRule(latest, rule.id, patch))
              }
              onRemove={() =>
                update((latest) => removeTaskRule(latest, rule.id))
              }
            />
          ))}
        </ul>
      )}
      <TasksPageNotice />
      <Button
        size="sm"
        variant="outline"
        disabled={!canUpdate || !definition.sheets.length || full}
        onClick={() =>
          update((latest) => addTaskRule(latest, crypto.randomUUID()))
        }
      >
        <PlusIcon />
        <T context="workbook add task rule">Add task rule</T>
      </Button>
      <WorkbookTaskList workbookId={editor.workbookId} />
    </section>
  );
}

/**
 * Where a Task rule's Tasks are found, decided as the top nav's Tasks tab
 * decides where it goes (`tasksHomePath`): nothing to say while the Tasks
 * queue is on; else the assignees' Inbox, else the Tasks preview. Each run
 * lists and links the Tasks it opened either way, as does the list below.
 */
function TasksPageNotice() {
  const [canReadTasks] = useCan(Action.Read, Subject.Task);
  const tasks = useFeatureFlag('tasks-enabled', false);
  const inbox = useFeatureFlag('inbox-enabled', false);
  const home = tasksHomePath({
    tasksEnabled: tasks.enabled && canReadTasks,
    inboxEnabled: inbox.enabled,
  });
  if (home === '/tasks') return null;
  const link = 'font-medium underline underline-offset-2';
  return (
    <p
      role="note"
      className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-800"
    >
      {home === '/inbox' ? (
        <T context="workbook task rules: tasks reach assignees in the inbox">
          Assignees find these tasks in their{' '}
          <Link to="/inbox" className={link}>
            Inbox
          </Link>
          . Open them from each run in Runs, or from Opened tasks below.
        </T>
      ) : (
        <T context="workbook task rules: the tasks queue is only a preview">
          Tasks is in{' '}
          <Link to="/preview/tasks" className={link}>
            preview
          </Link>{' '}
          for your organization, so there is no Tasks queue to list these in
          yet. Open them from each run in Runs, or from Opened tasks below.
        </T>
      )}
    </p>
  );
}

function TaskRuleRow({
  rule,
  definition,
  disabled,
  onChange,
  onRemove,
}: {
  rule: WorkbookTaskRule;
  definition: WorkbookDefinition;
  disabled: boolean;
  onChange: (patch: Partial<WorkbookTaskRule>) => void;
  onRemove: () => void;
}) {
  const gt = useGT();
  const priorities = useTaskPriorityLabels();
  const sheet = definition.sheets.find((item) => item.id === rule.sheetId);
  const columns = taskTitleColumns(sheet);
  const titleColumn =
    rule.titleColumn && columns.includes(rule.titleColumn)
      ? rule.titleColumn
      : columns[0];
  return (
    <li className="grid grid-cols-[repeat(4,minmax(0,1fr))_auto] items-end gap-2 rounded-md border p-2 text-sm">
      <label className="grid gap-1">
        <span className="text-xs text-muted-foreground">
          <T context="workbook task rule worksheet label">Worksheet</T>
        </span>
        <Select
          value={sheet ? rule.sheetId : undefined}
          disabled={disabled}
          onValueChange={(sheetId) =>
            onChange({ sheetId, titleColumn: undefined })
          }
        >
          <SelectTrigger
            aria-label={gt('Task rule worksheet', {
              context: 'workbook task rule worksheet picker',
            })}
          >
            <SelectValue
              placeholder={gt('Removed worksheet', {
                context: 'workbook task rule on a removed worksheet',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {definition.sheets.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <label className="grid gap-1">
        <span className="text-xs text-muted-foreground">
          <T context="workbook task rule title column label">Task title</T>
        </span>
        <Select
          value={titleColumn}
          disabled={disabled || !columns.length}
          onValueChange={(column) => onChange({ titleColumn: column })}
        >
          <SelectTrigger
            aria-label={gt('Task title column', {
              context: 'workbook task rule title column picker',
            })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {columns.map((column) => (
              <SelectItem key={column} value={column}>
                {column}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <label className="grid gap-1">
        <span className="text-xs text-muted-foreground">
          <T context="workbook task rule assignee label">Assignee</T>
        </span>
        <UserSelect
          value={rule.assigneeUserId}
          disabled={disabled}
          onChange={(assigneeUserId) => onChange({ assigneeUserId })}
        />
      </label>
      <label className="grid gap-1">
        <span className="text-xs text-muted-foreground">
          <T context="workbook task rule priority label">Priority</T>
        </span>
        <Select
          value={rule.priority}
          disabled={disabled}
          onValueChange={(priority) => {
            const chosen = TASK_PRIORITIES.find((item) => item === priority);
            if (chosen) onChange({ priority: chosen });
          }}
        >
          <SelectTrigger
            aria-label={gt('Task priority', {
              context: 'workbook task rule priority picker',
            })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TASK_PRIORITIES.map((priority) => (
              <SelectItem key={priority} value={priority}>
                {priorities[priority]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <IconTooltip>
        <Button
          size="icon"
          variant="ghost"
          className="size-8"
          disabled={disabled}
          aria-label={gt('Remove task rule', {
            context: 'workbook remove task rule',
          })}
          onClick={onRemove}
        >
          <TrashIcon />
        </Button>
      </IconTooltip>
    </li>
  );
}

/** The Tasks the Workbook opened, newest first, each linking to its Task. */
function WorkbookTaskList({ workbookId }: { workbookId: string }) {
  const statusLabel = useTaskStatusLabel();
  const tasks = useQuery(
    orpc.workbooks.tasks.queryOptions({ input: { workbookId } }),
  );
  if (tasks.isPending || tasks.error || !tasks.data.length) return null;
  return (
    <div className="space-y-1">
      <h4 className="text-xs font-medium text-muted-foreground">
        <T context="workbook opened tasks heading">Opened tasks</T>
      </h4>
      <ul className="divide-y rounded-md border">
        {tasks.data.map((task) => (
          <li
            key={task.id}
            className="flex items-center gap-2 px-2 py-1.5 text-sm"
          >
            <Link
              to="/tasks/$taskId"
              params={{ taskId: task.id }}
              className="min-w-0 flex-1 truncate text-primary underline underline-offset-2"
            >
              {task.displayId
                ? `${task.displayId} · ${task.title}`
                : task.title}
            </Link>
            <Badge variant="outline">{statusLabel(task.status)}</Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}
